import { getStatusMeta, StatusMeta } from '../../utils/statusMeta';

/**
 * Project workflow as the editor sees it: a stepper position for each project
 * status, a one-line "what happens next", deadline countdowns, and revision
 * versions with the creator's decision on each.
 *
 * Words: a *revision* is an uploaded cut ("v2"); a *change round* is the
 * plan's allowance of change requests ("1 of 2 change rounds used").
 */

export const WORKFLOW_STEPS = [
  'Assigned',
  'Accepted',
  'Editing',
  'In review',
  'Changes requested',
  'Approved',
  'Delivered',
] as const;

export type StepState = 'done' | 'active' | 'attention' | 'upcoming' | 'skipped';

const STATUS_STEP: Record<string, number> = {
  VIDEO_SUBMITTED: 0,
  EDITOR_ASSIGNED: 0,
  REQUIREMENTS_REVIEW: 1,
  EDITING_IN_PROGRESS: 2,
  DRAFT_READY: 3,
  CLIENT_REVIEW: 3,
  REVISION_REQUESTED: 4,
  FINAL_APPROVAL: 5,
  DELIVERED: 6,
};

const CHANGES_STEP = 4;

export interface WorkflowState {
  activeIndex: number;
  states: StepState[];
  cancelled: boolean;
}

/** Stepper position. "Changes requested" is only marked done if a round happened. */
export const workflowState = (status?: string | null, revisionCount = 0): WorkflowState => {
  const key = String(status || '').toUpperCase();
  if (key === 'CANCELLED') {
    return { activeIndex: -1, states: WORKFLOW_STEPS.map(() => 'upcoming'), cancelled: true };
  }
  const activeIndex = STATUS_STEP[key] ?? 0;
  const delivered = key === 'DELIVERED';
  const states = WORKFLOW_STEPS.map((_, index): StepState => {
    if (delivered) return index === CHANGES_STEP && revisionCount === 0 ? 'skipped' : 'done';
    if (index === activeIndex) return index === CHANGES_STEP ? 'attention' : 'active';
    if (index < activeIndex) {
      return index === CHANGES_STEP && revisionCount === 0 ? 'skipped' : 'done';
    }
    return 'upcoming';
  });
  return { activeIndex, states, cancelled: false };
};

/** Statuses where the assigned editor still has to accept or decline. */
export const awaitingEditorResponse = (status?: string | null, hasEditor = true) => {
  const key = String(status || '').toUpperCase();
  return key === 'EDITOR_ASSIGNED' || (key === 'VIDEO_SUBMITTED' && hasEditor);
};

/** The stepper's word for REVISION_REQUESTED: the creator asked for changes to a cut. */
export const CHANGES_REQUESTED_LABEL = WORKFLOW_STEPS[4];
export const AWAITING_RESPONSE_LABEL = 'Awaiting your response';

/**
 * A project's status as a chip on the editor side (the project list and the
 * workspace header). Labels come from utils/statusMeta, except that:
 *  - an assignment the editor hasn't answered reads "Awaiting your response";
 *  - REVISION_REQUESTED reads "Changes requested", the stepper's words, so
 *    one status has one name across the stepper, the chip and the list.
 * Tones keep statusMeta's meaning ("attention" = waiting on the person
 * looking at it), seen from the viewer's side: for the assigned editor an
 * unanswered assignment and requested changes need them; a cut with the
 * creator doesn't.
 */
export const projectStatusDisplay = (
  status: string | null | undefined,
  { viewer = 'editor', hasEditor = true }: { viewer?: 'creator' | 'editor' | 'unauthorized'; hasEditor?: boolean } = {}
): StatusMeta => {
  const key = String(status || '').trim().toUpperCase();
  const meta = getStatusMeta('project', status);
  if (viewer === 'editor') {
    if (awaitingEditorResponse(key, hasEditor)) return { label: AWAITING_RESPONSE_LABEL, tone: 'attention' };
    if (key === 'REVISION_REQUESTED') return { label: CHANGES_REQUESTED_LABEL, tone: 'attention' };
    if (key === 'DRAFT_READY' || key === 'CLIENT_REVIEW') return { label: meta.label, tone: 'neutral' };
    return meta;
  }
  if (key === 'REVISION_REQUESTED') return { label: CHANGES_REQUESTED_LABEL, tone: meta.tone };
  return meta;
};

interface GuidanceContext {
  role: 'creator' | 'editor' | 'unauthorized';
  /** Versions uploaded so far. */
  versionCount: number;
  revisionCount?: number;
  revisionLimit?: number;
}

/** One sentence on what happens next, for the person looking at the page. */
export const nextStepGuidance = (status: string | null | undefined, context: GuidanceContext): string => {
  const key = String(status || '').toUpperCase();
  const latest = context.versionCount > 0 ? `v${context.versionCount}` : 'the first cut';
  const next = `v${context.versionCount + 1}`;
  const roundsLeft = typeof context.revisionLimit === 'number'
    ? Math.max(0, context.revisionLimit - (context.revisionCount ?? 0))
    : null;

  if (key === 'CANCELLED') return 'This project was cancelled. It is read-only; downloads stay available.';
  if (key === 'DELIVERED') return 'Delivered. The project is complete and read-only; downloads stay available.';

  if (context.role === 'creator') {
    switch (key) {
      case 'VIDEO_SUBMITTED':
      case 'EDITOR_ASSIGNED':
        return 'Waiting for the editor to accept the project.';
      case 'REQUIREMENTS_REVIEW':
      case 'EDITING_IN_PROGRESS':
        return 'The editor is working through your feedback. You will be notified when a version is ready.';
      case 'DRAFT_READY':
      case 'CLIENT_REVIEW':
        return `${latest} is waiting for your review. Watch it, then approve it or request changes.`;
      case 'REVISION_REQUESTED':
        return `You requested changes. The editor will upload ${next}.`;
      case 'FINAL_APPROVAL':
        return `You approved ${latest}. The project will be marked delivered.`;
      default:
        return 'The editor is working on this project.';
    }
  }

  switch (key) {
    case 'VIDEO_SUBMITTED':
    case 'EDITOR_ASSIGNED':
      return 'Read the brief and the feedback, then accept or decline the project.';
    case 'REQUIREMENTS_REVIEW':
      return 'Accepted. Work through the tasks, then upload your first cut for the creator to review.';
    case 'EDITING_IN_PROGRESS':
      return context.versionCount > 0
        ? `Keep working, then upload ${next} and submit it for review.`
        : 'Work through the tasks, then upload your first cut and submit it for review.';
    case 'DRAFT_READY':
    case 'CLIENT_REVIEW':
      return `${latest} is with the creator. You will be notified when they approve it or request changes.`;
    case 'REVISION_REQUESTED':
      return `Changes requested on ${latest}. Address the creator's comments, then upload ${next}.` +
        (roundsLeft !== null && Number.isFinite(roundsLeft)
          ? ` ${roundsLeft} change round${roundsLeft === 1 ? '' : 's'} left on this plan.`
          : '');
    case 'FINAL_APPROVAL':
      return `The creator approved ${latest}. Nothing else is needed from you.`;
    default:
      return 'Work through the tasks, then upload a revision for review.';
  }
};

export type DeadlineTone = 'default' | 'warning' | 'error' | 'success';

/** Date-only deadlines ("2026-10-04") mean the end of that day, local time. */
export const parseDeadline = (deadline?: string | null): Date | null => {
  if (!deadline) return null;
  const text = String(deadline);
  const date = /^\d{4}-\d{2}-\d{2}$/.test(text) ? new Date(`${text}T23:59:59`) : new Date(text);
  return Number.isNaN(date.getTime()) ? null : date;
};

const DAY_MS = 24 * 60 * 60 * 1000;

/** "in 3 days", "due today", "overdue by 2 days" with an urgency tone. */
export const describeDeadline = (
  deadline: string | null | undefined,
  now: Date = new Date(),
  finished = false
): { relative: string; tone: DeadlineTone } | null => {
  const due = parseDeadline(deadline);
  if (!due) return null;
  if (finished) return { relative: 'project finished', tone: 'success' };
  const msLeft = due.getTime() - now.getTime();
  const startOf = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const dayDiff = Math.round((startOf(due) - startOf(now)) / DAY_MS);
  if (msLeft < 0) {
    const daysLate = Math.max(1, -dayDiff);
    return { relative: dayDiff === 0 ? 'overdue today' : `overdue by ${daysLate} day${daysLate === 1 ? '' : 's'}`, tone: 'error' };
  }
  if (dayDiff === 0) {
    const hours = Math.max(1, Math.round(msLeft / (60 * 60 * 1000)));
    return { relative: `due today · ${hours}h left`, tone: 'error' };
  }
  if (dayDiff === 1) return { relative: 'due tomorrow', tone: 'warning' };
  return { relative: `in ${dayDiff} days`, tone: dayDiff <= 3 ? 'warning' : 'default' };
};

export interface RevisionLike {
  id: string;
  filename?: string | null;
  createdBy?: string | null;
  createdByName?: string | null;
  createdAt?: string | null;
  status?: string | null;
  previewUrl?: string | null;
  notes?: string | null;
  sizeBytes?: number | null;
}

export interface NoteLike {
  id: string;
  message: string;
  authorId?: string | null;
  authorName?: string | null;
  authorRole?: string | null;
  timestamp?: string | null;
  type?: string | null;
}

export interface RevisionVersion<R extends RevisionLike = RevisionLike> {
  revision: R;
  version: number;
  /** The creator's accept / request-changes comments on this version. */
  decisions: NoteLike[];
}

const timeOf = (value?: string | null) => {
  const ms = value ? new Date(value).getTime() : NaN;
  return Number.isNaN(ms) ? 0 : ms;
};

/**
 * Versions numbered by upload order (v1 = first upload), newest first. The
 * backend files a reviewer's comments as a project note whose id starts with
 * `rev_<revisionId>_`, which is how they are matched to their version.
 */
export const buildRevisionVersions = <R extends RevisionLike>(revisions: R[], notes: NoteLike[] = []): RevisionVersion<R>[] => {
  const ordered = revisions
    .filter(Boolean)
    .map((revision, index) => ({ revision, index }))
    .sort((a, b) => timeOf(a.revision.createdAt) - timeOf(b.revision.createdAt) || b.index - a.index);
  const versions = ordered.map(({ revision }, position) => ({
    revision,
    version: position + 1,
    decisions: notes
      .filter((note) => typeof note?.id === 'string' && note.id.startsWith(`rev_${revision.id}_`))
      .sort((a, b) => timeOf(a.timestamp) - timeOf(b.timestamp)),
  }));
  return versions.reverse();
};

/**
 * A cut's status in the app's own words (utils/statusMeta), so it reads the
 * same here as on the creator's page: an accepted cut is "Approved". A cut
 * with no status yet counts as uploaded.
 */
export const revisionStatusMeta = (status?: string | null): StatusMeta =>
  getStatusMeta('revision', String(status || '').trim() || 'UPLOADED');
